import CAModal from '@ds/components/containers/CAModal'
import CASelectDropdown from '@ds/components/form-elements/CASelectDropdown'
import type { CheckedNodes } from '@vuejs/components/shared/TreeView'
import api from '@vuejs/services/api'
import { moveContentPackageOrFolder } from '@vuejs/services/packages/'
import type { ContentPackageFolder } from '@vuejs/services/packages/interfaces'
import { useGlobalStore } from '@vuejs/stores/GlobalStore'
import { useNotificationStore } from '@vuejs/stores/NotificationStore'
import { usePackagesAndProgramsStore } from '@vuejs/stores/PackagesAndProgramsStore'
import { defineComponent, type PropType } from 'vue'

export default defineComponent({
	name: 'MoveIntoFolderModal',

	props: {
		folders: { required: true, type: Array as PropType<ContentPackageFolder[]> },
		items: { required: true, type: Array as PropType<CheckedNodes[]> },
	},

	emits: {
		cancel: () => true,
		move: (_id: number | null) => true,
	},

	computed: {
		globalStore(): ReturnType<typeof useGlobalStore> {
			return useGlobalStore()
		},

		notificationStore(): ReturnType<typeof useNotificationStore> {
			return useNotificationStore()
		},

		packagesAndProgramsStore() {
			return usePackagesAndProgramsStore()
		},
	},

	data(): Data {
		return { destinationFolderId: null }
	},

	methods: {
		closeModal() {
			this.globalStore.resetCAModal()
			this.destinationFolderId = null
			this.$emit('cancel')
		},

		async moveIntoFolder() {
			if (!this.items.length) {
				this.notificationStore.onHandleErrorMessages({ error: { message: 'Nothing to move. Please select item(s) and try again' } })

				return
			}

			try {
				await moveContentPackageOrFolder(api, {
					id: this.destinationFolderId,
					items: this.items,
				})
				this.$emit('move', this.destinationFolderId)
				this.closeModal()
				this.notificationStore.onHandleSuccessMessages({ message: 'Selected content package(s)/folder(s) have been moved' })
			} catch (error) {
				this.notificationStore.onHandleErrorMessages({ error: { message: error instanceof Error ? error.message : String(error) } })
			}
		},
	},

	render() {
		return (
			<CAModal
				alignment="left-aligned"
				isOpen={this.globalStore.emittedEvent === 'move-into-folder-modal'}
				submitText="Move"
				title="Move into folder"
				onCancel={this.closeModal}
				onSubmit={this.moveIntoFolder}
			>
				<CASelectDropdown
					dataTest="destination-folder-select"
					id="destination-folder-id"
					label="Select folder to move package(s) or folder(s)"
					options={this.packagesAndProgramsStore.allFolders.map((folder: ContentPackageFolder) => ({
						id: `${folder.id}`,
						label: folder.level_name ?? folder.name,
					}))}
					required={true}
					v-model={this.destinationFolderId}
				/>
			</CAModal>
		)
	},
})

interface Data {
	destinationFolderId: number | null
}
